import React, { createContext, useContext, useCallback, useState, useEffect } from 'react'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { SimpleNotification } from '@/components/SimpleNotification'
import { useTranslation } from '@/i18n'
import { unifiedNotificationManager as notificationManager } from '@2cmstudio/notifications'

// Mock analytics for now
const useAnalytics = () => {
  const trackEvent = useCallback((event: string, data?: any) => {
    if (import.meta.env.DEV) {
      console.log('Analytics event:', event, data)
    }
  }, [])
  
  return { trackEvent }
}

// Check if Capacitor is available
const isCapacitorAvailable = typeof window !== 'undefined' && (window as any).Capacitor
const Capacitor = isCapacitorAvailable ? (window as any).Capacitor : { isNativePlatform: () => false }

// Mock LocalNotifications if not available
const LocalNotifications = isCapacitorAvailable ? (window as any).Capacitor?.Plugins?.LocalNotifications : {
  checkPermissions: () => Promise.resolve({ display: 'prompt' }),
  requestPermissions: () => Promise.resolve({ display: 'granted' }),
  schedule: () => Promise.resolve(),
  cancel: () => Promise.resolve()
}

// Using local notifications only - no push notification service needed

// Firebase messaging removed - using local notifications only

interface NotificationOptions {
  title: string
  body: string
  data?: any
  scheduleAt?: Date
  id?: string
}

interface InAppNotification {
  id: string
  message: string
  type: 'info' | 'success' | 'warning' | 'error'
  duration?: number
}

interface NotificationContextValue {
  // Push notifications
  requestPermission: () => Promise<boolean>
  scheduleNotification: (options: NotificationOptions) => Promise<void>
  cancelNotification: (id: string) => Promise<void>
  isPushSupported: boolean
  permissionStatus: 'granted' | 'denied' | 'prompt' | null
  
  // In-app notifications
  showNotification: (notification: Omit<InAppNotification, 'id'>) => void
  hideNotification: (id: string) => void
  notifications: InAppNotification[]
}

const NotificationContext = createContext<NotificationContextValue | null>(null)

export const useNotifications = () => {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider')
  }
  return context
}

export const UnifiedNotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth()
  const { trackEvent } = useAnalytics()
  const { t } = useTranslation()
  
  const [notifications, setNotifications] = useState<InAppNotification[]>([])
  const [permissionStatus, setPermissionStatus] = useState<'granted' | 'denied' | 'prompt' | null>(null)
  
  const isNative = Capacitor.isNativePlatform()
  const isPushSupported = isNative || ('Notification' in window && 'serviceWorker' in navigator)

  // Initialize notification manager and check permissions
  useEffect(() => {
    const initializeNotifications = async () => {
      // Initialize the notification manager with service worker configuration
      const initialized = await notificationManager.initialize({
        web: {
          serviceWorkerPath: '/service-worker.js'
        }
      })
      
      if (initialized) {
        console.log('✅ Notification manager initialized successfully')
        
        // Check permissions after initialization
        if (isNative) {
          const result = await LocalNotifications.checkPermissions()
          const status = result.display
          setPermissionStatus(status === 'granted' || status === 'denied' || status === 'prompt' ? status : 'prompt')
        } else {
          const permission = notificationManager.getPermissionStatus()
          setPermissionStatus(permission === 'granted' || permission === 'denied' || permission === 'default' ? 
            (permission === 'default' ? 'prompt' : permission) : 'prompt')
        }
      } else {
        console.warn('⚠️ Failed to initialize notification manager')
      }
    }
    
    initializeNotifications()
  }, [isNative])



  // Request notification permission
  const requestPermission = useCallback(async (): Promise<boolean> => {
    try {
      trackEvent('notification_permission_requested')
      
      if (isNative) {
        const result = await LocalNotifications.requestPermissions()
        const granted = result.display === 'granted'
        const status = result.display
        setPermissionStatus(status === 'granted' || status === 'denied' || status === 'prompt' ? status : 'prompt')
        trackEvent('notification_permission_response', { granted })
        return granted
      } else {
        // Use the unified notification manager for web browsers
        const result = await notificationManager.requestPermission()
        const granted = result.granted
        setPermissionStatus(result.permission === 'granted' || result.permission === 'denied' || result.permission === 'default' ? 
          (result.permission === 'default' ? 'prompt' : result.permission) : 'prompt')
        trackEvent('notification_permission_response', { granted })
        return granted
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error)
      return false
    }
  }, [isNative, user, trackEvent])

  // Schedule a notification
  const scheduleNotification = useCallback(async (options: NotificationOptions) => {
    const { title, body, data, scheduleAt, id } = options
    
    if (permissionStatus !== 'granted') {
      const granted = await requestPermission()
      if (!granted) {
        showNotification({
          message: t('notifications.permissionRequired'),
          type: 'warning'
        })
        return
      }
    }

    try {
      if (isNative) {
        // Use Capacitor Local Notifications
        await LocalNotifications.schedule({
          notifications: [{
            id: id ? parseInt(id) : Date.now(),
            title,
            body,
            schedule: scheduleAt ? { at: scheduleAt } : undefined,
            extra: data
          }]
        })
      } else {
        // Use unified notification manager for web browsers
        const notificationPayload = {
          title,
          body,
          data,
          tag: id,
          icon: '/logo-192x192.avif',
          badge: '/logo-192x192.avif'
        }
        
        if (scheduleAt) {
          // Schedule for later
          await notificationManager.schedule({
            ...notificationPayload,
            scheduleTime: scheduleAt,
            id: id || `notification-${Date.now()}`
          })
        } else {
          // Send immediately
          await notificationManager.sendImmediate(notificationPayload)
        }
      }
      
      trackEvent('notification_scheduled', {
        isScheduled: !!scheduleAt,
        platform: isNative ? 'native' : 'web'
      })
    } catch (error) {
      console.error('Error scheduling notification:', error)
      showNotification({
        message: t('notifications.scheduleFailed'),
        type: 'error'
      })
    }
  }, [isNative, user, permissionStatus, requestPermission, trackEvent, t])

  // Cancel a notification
  const cancelNotification = useCallback(async (id: string) => {
    try {
      if (isNative) {
        await LocalNotifications.cancel({ notifications: [{ id: parseInt(id) }] })
      } else {
        // Use unified notification manager for web browsers
        await notificationManager.cancel(id)
      }
      
      trackEvent('notification_cancelled')
    } catch (error) {
      console.error('Error cancelling notification:', error)
    }
  }, [isNative, user, trackEvent])

  // In-app notification management
  const showNotification = useCallback((notification: Omit<InAppNotification, 'id'>) => {
    const id = Date.now().toString()
    setNotifications(prev => [...prev, { ...notification, id }])
  }, [])

  const hideNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id))
  }, [])

  // Listen for FCM messages
  useEffect(() => {
    const handleFCMNotification = (event: CustomEvent) => {
      console.log('🔔 FCM notification received in UnifiedNotificationProvider:', event.detail)
      
      const { title, body, type = 'info' } = event.detail
      
      showNotification({
        message: `${title}: ${body}`,
        type: type as 'info' | 'success' | 'warning' | 'error'
      })
    }

    window.addEventListener('show-in-app-notification', handleFCMNotification as EventListener)
    
    return () => {
      window.removeEventListener('show-in-app-notification', handleFCMNotification as EventListener)
    }
  }, [showNotification])

  const value: NotificationContextValue = {
    requestPermission,
    scheduleNotification,
    cancelNotification,
    isPushSupported,
    permissionStatus,
    showNotification,
    hideNotification,
    notifications
  }

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <div className="fixed bottom-4 right-4 z-50 space-y-2">
        {notifications.map(notification => (
          <SimpleNotification
            key={notification.id}
            title=""
            body={notification.message}
            type={notification.type}
            duration={notification.duration}
            onClose={() => hideNotification(notification.id)}
          />
        ))}
      </div>
    </NotificationContext.Provider>
  )
}